import React, { useEffect } from 'react'
import { Router, Route } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import { useDispatch, useSelector } from 'react-redux'

import KioskApp from './kiosk/App'

import { customHistory } from './stores'

import AppRoutes from './AppRoutes'
import ErrorBoundary from './ErrorBoundary'

import { setErrorInfo } from './actions/appState'

import { VNWebSDKDataBridge } from './VNAndroidSDK/containers/VNWebSDKDataBridge'
import { VNCustomerFacingDisplay } from './VNCustomerFacingDisplay/containers/VNCustomerFacingDisplay'
import { VNModeSelect } from './VNMode/containers/VNModeSelect'
import { VNThemes, THEMES } from 'vn-react-components/VNThemes'
import { VNSnackbarSystem } from 'vn-react-components/VNSnackbarSystem/containers/VNSnackbarSystem'
import { getDeviceMode } from './VNMode/Selectors'

import createDataDogLog from './utils/datadog'
import { ToastManager } from './utils/toastManager'

// eslint-disable-next-line no-unused-vars
import { VNDebugger } from './VNCustomerFacingDisplay/containers/VNDebugger'
import { VNDialogsContainer } from './VNDialogs/containers/VNDialogsContainer'
import { VNPOSListeners } from './VNCustomerFacingDisplay/containers/VNPOSListeners'
import { VNBalanceDue } from './VNCustomerFacingDisplay/containers/VNBalanceDue'
import { VNPPI } from './VNPPI/containers/VNPPI'
import { VNMobileScannerSetup } from './VNMobileScanner/containers/VNMobileScannerSetup'
import { VNInvoiceManager } from './VNInvoiceManager/containers/VNInvoiceManager'
import { VNSyncManager } from './VNSyncManager/containers/VNSyncManager'
import { VNDataLoadManager } from './VNDataLoadManager/containers/VNDataLoadManager'
import { VNKeyboardActivity } from './VNAndroidSDK/VNKeyboardActivity'
import { VNUpdateManager } from './VNUpdateManager/containers/VNUpdateManager'

import VNSpinnerManager from './VNLoader/components/VNSpinnerManager'
import VNRouteListener from './VNRoutes/components/VNRoutes'
import { VNConfigurations } from './VNConfigurations/containers/VNConfigurations'
import { VNGlobalConfigurations } from './VNConfigurations/containers/VNGlobalConfigurations'
import VNOrders from './VNOrders/containers/VNOrders'
import { VNORDERS_FEATURE_ENABLED } from './VNOrders/Enums'
import { isCFDMode, isTestMode } from './VNAndroidSDK/Utils'

import ImageVersioning from './ImageVersioning'
import { MODES } from './VNMode/Reducer'
import { getDebuggerPassthroughEnabled } from './VNTestMode/Selectors'
import VNManagerPin from './VNManagerApproval/components/VNManagerPin'
import { setDeviceMode } from './VNMode/ActionCreators'
import { VNCartProvider } from './VNItems/hooks/VNCartContext'

const App = () => {
  const dispatch = useDispatch()

  const cfdMode = useSelector((state) => getDeviceMode(state))
  const isDebuggerEnabled = useSelector(getDebuggerPassthroughEnabled)
  useEffect(() => {
    window.localStorage.setItem('cfd_web_test_mode', false)
    if (!isTestMode()) return
    if (cfdMode !== MODES.POS && window.location.pathname !== '/customer-facing-display') return
    if (window.location.pathname === '/customer-facing-display') {
      dispatch(setDeviceMode(MODES.CFD))
    }
    window.localStorage.setItem('cfd_web_test_mode', true)
    const channel = new BroadcastChannel('cfd_web_test_mode')
    window.Broadcaster = channel
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfdMode])

  const handleError = (error) => {
    // prevent errors from apeparing on CFD devices (i.e. A3700)
    // the redirect to / to ensure a fresh valid token
    if (isCFDMode()) {
      createDataDogLog('error', `[CFD] Unhandled error occurred: ${error?.message ?? 'N/A error message'}`, {
        error,
      })

      customHistory.push('/?apk=true&cfd=true')
      return
    }

    // Show a toast to the user and log event in data dog
    ToastManager.error('An unexpected error occurred. Please try again later.', {
      position: toast.POSITION.BOTTOM_RIGHT,
      autoClose: false,
    })

    createDataDogLog('error', `An unexpected error occurred: ${error?.message ?? 'N/A error message'}`, {
      error,
    })

    if (error && typeof error.preventDefault === 'function') {
      dispatch(
        setErrorInfo({
          message: error.message,
          stack: error.stack,
        }),
      )
      error.preventDefault()
    }

    customHistory.push('/device-login')
  }

  window.onerror = handleError
  window.addEventListener('error', handleError)

  const displayVNOrders = () => {
    if (VNORDERS_FEATURE_ENABLED) {
      return <VNOrders />
    }
  }

  return (
    <Router history={customHistory}>
      <ToastContainer
        id="toast-container"
        position="bottom-left"
        draggable={false}
        hideProgressBar={true}
        closeOnClick={true}
        autoClose={2500}
        newestOnTop={false}
        limit={3}
        style={{ zIndex: 999999 }}
      />

      <VNConfigurations />
      <VNGlobalConfigurations />
      <ErrorBoundary handleError={handleError} cfdMode={cfdMode}>
        <VNWebSDKDataBridge>
          <VNThemes theme={THEMES.INTERNAL}>
            <ImageVersioning>
              <VNCartProvider>
                <>
                  {isDebuggerEnabled && <VNDebugger />}
                  <Route exact path="/" render={(props) => <VNModeSelect {...props} />} />
                  <Route path="/customer-facing-display" render={(props) => <VNCustomerFacingDisplay {...props} />} />
                  <Route path="/pos/balance-due" render={(props) => <VNBalanceDue {...props} />} />
                  <Route path="/kiosk" component={KioskApp} />
                  {displayVNOrders()}
                  <VNPPI />
                  <VNRouteListener />
                  <AppRoutes />
                  <VNDialogsContainer />
                  <VNPOSListeners />
                  <VNMobileScannerSetup />
                  <VNSnackbarSystem />
                  <VNManagerPin />
                  <VNKeyboardActivity />
                  <VNInvoiceManager />
                  <VNSyncManager />
                  <VNDataLoadManager />
                  <VNUpdateManager />
                  <VNSpinnerManager />
                </>
              </VNCartProvider>
            </ImageVersioning>
          </VNThemes>
        </VNWebSDKDataBridge>
      </ErrorBoundary>
    </Router>
  )
}

export default App
